import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Layout, Typography, Space, Row, Col } from 'antd';
import ReportQueryBuilder from './ReportQueryBuilder';
import ReportStatusDisplay from './ReportStatusDisplay';
import ReportViewer from './ReportViewer';
import ReportHistory from './ReportHistory';
import ReportErrorBoundary from './ReportErrorBoundary';
import { useReportPolling } from '../../hooks/useReportPolling';
import { stopPolling, fetchReportHTML } from '../../redux/slices/aiReportsSlice';
import styles from './ReportsPage.module.css';

const { Content } = Layout;
const { Title } = Typography;

const ReportsPage = ({ user }) => {
  const dispatch = useDispatch();
  const {
    reportId,
    reportStatus,
    reportData,
    htmlContent,
    htmlLoading,
    error,
    pollingAttempts,
    isDownloading,
  } = useSelector((state) => state.aiReports);

  // Use custom polling hook
  useReportPolling();

  // Fetch HTML content after report generation completes
  useEffect(() => {
    if (reportStatus === 'completed' && reportId && !htmlContent && !htmlLoading) {
      dispatch(fetchReportHTML(reportId));
    }
  }, [dispatch, reportStatus, reportId, htmlContent, htmlLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(stopPolling());
    };
  }, [dispatch]);

  return (
    <ReportErrorBoundary>
      <Layout className={styles.reportsPage}>
        <Content className={styles.content}>
          <div className={styles.container}>
            <Title level={2}>AI-Powered Reports</Title>
            <Typography.Paragraph className={styles.description}>
              Ask questions about your healthcare data in plain English.
              AI will translate your query and generate comprehensive reports with tables and charts.
            </Typography.Paragraph>

            <Row gutter={24}>
              {/* Main content */}
              <Col xs={24} lg={18}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {/* Query Builder */}
                  <ReportQueryBuilder user={user} />

                  {/* Status Display */}
                  <ReportStatusDisplay
                    status={reportStatus}
                    error={error}
                    pollingAttempts={pollingAttempts}
                  />

                  {/* Report Viewer */}
                  {reportStatus === 'completed' && reportData && (
                    <ReportViewer
                      reportData={reportData}
                      reportId={reportId}
                      isDownloading={isDownloading}
                      htmlContent={htmlContent}
                      htmlLoading={htmlLoading}
                    />
                  )}
                </Space>
              </Col>

              {/* History sidebar */}
              <Col xs={24} lg={6}>
                <ReportHistory />
              </Col>
            </Row>
          </div>
        </Content>
      </Layout>
    </ReportErrorBoundary>
  );
};

export default ReportsPage;
